import React from 'react';

import { BrowserRouter as Router } from 'react-router-dom';

import '@/Shared/UI/Styles/App.css';
import '@/Shared/UI/Forms/form.css';

import { SideBarMenu, PageLoader, useAppInitializer, Page, ActionStatusHelper } from '@/Shared';
import { useAppSelector } from './OneLiners';

/**
 * =====================================================
 * App
 * =====================================================
 *
 * Main application entry point.
 *
 * This component is responsible for:
 * - Bootstrapping the application via `useAppInitializer`
 * - Handling global loading and error states
 * - Rendering the main application layout once ready
 *
 * The App component acts as a thin orchestration layer:
 * it does not contain business logic, only UI flow control
 * based on the global initialization state.
 *
 * Author: Samano CASTRE
 * Date: July 16, 2025
 */
function App() {

	const appIsReady = useAppSelector(state => state.appState.appIsReady);

	/**
	 * =====================================================
	 * Application initialization state
	 * =====================================================
	 *
	 * `useAppInitializer` centralizes the full bootstrap logic:
	 * - translations
	 * - visitor challenge
	 * - session validation
	 * - enums & user session loading
	 *
	 * It exposes a normalized ActionResponse-like object.
	 */
	const { error, status } = useAppInitializer();


	/**
	 * =====================================================
	 * Error state
	 * =====================================================
	 *
	 * If an unrecoverable error occurred during initialization,
	 * we display the error message and allow a full reload.
	 *
	 * This is intentionally simple: initialization errors
	 * usually indicate a broken or inconsistent app state.
	 */
	if (error) {
		return (
			<div> 
				{error}
				<button onClick={() => window.location.reload()}>
					Réessayer
				</button>
			</div>
		);
	}

	/**
	 * =====================================================
	 * Loading state
	 * =====================================================
	 *
	 * While the application is initializing, display a
	 * blocking loader to prevent rendering incomplete UI.
	 */
	if (ActionStatusHelper.isLoading(status) || !appIsReady) {
		return (
			<PageLoader
				message="[AMAT] - Chargement de l'application. Veuillez patienter. Merci"
			/>
		);
	}


	/**
	 * =====================================================
	 * Ready state
	 * =====================================================
	 *
	 * Once initialization succeeds, render the full
	 * application layout:
	 * - Router
	 * - Sidebar navigation
	 * - Main page content
	 */
	return (
		<Router>
			<main className="App">
				<SideBarMenu />
				<Page />
			</main>
		</Router>
	);
}

export default App;
